import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { User } from '../contexts/AuthContext';
import Dashboard from './Dashboard';
import OperatorDashboard from './OperatorDashboard';
import SupervisorDashboard from './SupervisorDashboard';
import ClientDashboard from './ClientDashboard';
import SueldoWidget from './SueldoWidget';
import AuditTracking from './AuditTracking';

interface ImpersonationDashboardProps {
  adminUser: User;
  targetUser: any;
  onExit: () => void;
}

const ImpersonationDashboard: React.FC<ImpersonationDashboardProps> = ({ 
  adminUser, 
  targetUser, 
  onExit 
}) => {
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Crear un usuario impersonado con los datos del usuario objetivo
    const createImpersonatedUser = () => {
      const impersonated: User = {
        id: targetUser.id,
        name: targetUser.name,
        email: targetUser.email,
        role: targetUser.role,
        isActive: targetUser.isActive,
        permissions: targetUser.permissions || {},
        clienteId: targetUser.clienteId || null
      };
      
      setImpersonatedUser(impersonated);
      setLoading(false);
    };

    createImpersonatedUser();
  }, [targetUser]);

  if (loading || !impersonatedUser) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f8fafc'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div className="spinner" />
          <p>Cargando dashboard de {targetUser.name}...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      position: 'relative'
    }}>
      {/* Banner de impersonación */}
      <div style={{
        backgroundColor: '#dc2626',
        color: 'white',
        padding: '0.75rem',
        textAlign: 'center',
        fontSize: '0.875rem',
        fontWeight: '500',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: '1200px', margin: '0 auto' }}>
          <span>
            🚨 <strong>MODO IMPERSONACIÓN:</strong> Viendo como {impersonatedUser.name} ({impersonatedUser.role}) - Admin: {adminUser.name}
          </span>
          <button
            onClick={onExit}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              padding: '0.25rem 0.75rem',
              borderRadius: '4px',
              fontSize: '0.75rem',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            ✕ Salir de Impersonación
          </button>
        </div>
      </div>

      {/* Header con información del usuario impersonado */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '1rem 0',
        marginBottom: '1rem'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#3b82f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '1.25rem',
              fontWeight: 'bold'
            }}>
              {impersonatedUser.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 style={{ 
                fontSize: '1.5rem', 
                fontWeight: 'bold', 
                margin: 0, 
                color: '#1f2937' 
              }}>
                {impersonatedUser.name}
              </h1>
              <p style={{ 
                color: '#6b7280', 
                margin: '0.25rem 0 0 0', 
                fontSize: '0.875rem' 
              }}>
                {impersonatedUser.email} • {impersonatedUser.role}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard específico según el rol */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        {impersonatedUser.role === 'operador' && (
          <div>
            {/* Widget de sueldo para operador */}
            <SueldoWidget user={impersonatedUser} />
            
            {/* Dashboard principal */}
            <OperatorDashboard user={impersonatedUser} />
            
            {/* Tracking de auditorías */}
            <AuditTracking user={impersonatedUser} />
          </div>
        )}
        
        {impersonatedUser.role === 'supervisor' && (
          <div>
            {/* Dashboard supervisor restringido - SIN FINANZAS */}
            <SupervisorDashboard user={impersonatedUser} />
            
            {/* Tracking de auditorías */}
            <AuditTracking user={impersonatedUser} />
          </div>
        )}
        
        {impersonatedUser.role === 'cliente' && (
          <div>
            {/* Dashboard del cliente */}
            <ClientDashboard user={impersonatedUser} />
            
            {/* Tracking de sus auditorías */}
            <AuditTracking user={impersonatedUser} />
          </div>
        )}
        
        {(impersonatedUser.role === 'admin' || impersonatedUser.role === 'contable') && (
          <div>
            {/* Widget de sueldo para admin/contable */}
            <SueldoWidget user={impersonatedUser} />
            
            {/* Dashboard principal */}
            <Dashboard user={impersonatedUser} />
          </div>
        )}
      </div>

      {/* Estilos para spinner */}
      <style jsx>{`
        .spinner {
          border: 3px solid #f3f4f6;
          border-top: 3px solid #3b82f6;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          animation: spin 1s linear infinite;
          margin: 0 auto;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ImpersonationDashboard;